import { Trans } from "@lingui/macro";
import { Users } from 'lucide-react';
import { useEffect, useState } from "react";
import { useHistory } from '../../hooks/useRouterCompat';
import { Notification } from "../../models/Notification";
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from "../../services/MyNotifs";
import { twil } from '../../services/TwilioHelper';
import { SoundUtil } from '../../services/Utils/SoundUtil';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { UserAvatar } from './UserAvatar';

import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";

const CallInvitePopup: React.FC = () => {
  const [currentNotifs, setCurrentNotifs] = useState<Notification[]>([]);
  const [notificationHistory] = useState(new Map<string, Notification>());

  useEffect(() => {
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates(handleNotifications);
    return () => { unsubNotifs(); };
  }, [])

  if (currentNotifs.length === 0) return null;

  return (
    <div className='fixed inset-0 z-[100] flex flex-col items-center justify-center bg-overlay-heavy backdrop-blur-sm p-4 animate-in fade-in duration-200'>
      <div className="flex flex-col gap-4 w-full max-w-sm">
        {currentNotifs.map(n => <CallInviteItem notif={n} key={n.id} />)}
      </div>
    </div>
  )

  function handleNotifications(notifs: Notification[]) {
    let callNotifs = notifs.filter(n => n.isNew && n.type == 'CallStart');
    let _currentList: Notification[] = [];
    let played = false;
    callNotifs.forEach(n => {
      if (!(n.roomId && My.RoomSettings?.get(n.roomId)?.disableVoiceCallPopups)) {
        _currentList.push(n);
        if (!notificationHistory.get(n.id)) {
          notificationHistory.set(n.id, n);
          if (!played) {
            SoundUtil.playInvitedToCall();
            played = true;
          }
        }
      }
    })
    setCurrentNotifs(_currentList);
  }
};

const CallInviteItem: React.FC<{ notif: Notification }> = ({ notif }) => {
  const [call, setCall] = useState<RoomCall>();
  const history = useHistory();

  useEffect(() => {
    const minAgo = Util.nrMinAgo(notif.created);
    if (minAgo > 10) {
      console.log('call is 10 min old so dismiss');
      dismissCall();
    } else if (notif.roomId)
      Fire.call.getRoomCall(notif.roomId).then(c => {
        setCall(c);
        if (!c) {
          console.log('there is no roomcall so dismiss');
          dismissCall();
        }
      });
  }, []);

  if (!call) return <></>;

  const otherUser = call.room.isOneToOne ? Room.getOtherOne2OneUser(call.room, My.user.id) : undefined;

  return (
    <Card className="pointer-events-auto w-full border-0 shadow-2xl bg-background text-foreground overflow-hidden animate-in zoom-in-95 duration-200">
      <CardContent className="p-4 grid gap-3">
        {call?.room.isOneToOne ? (
          <div className="flex items-start gap-3">
            <UserAvatar
              src={otherUser?.photoUrl}
              name={otherUser?.name}
              className="w-10 h-10 mt-0.5"
            />
            <div>
              <p className="text-sm font-medium leading-tight">
                <span className="font-bold">{otherUser?.name}</span> <span className="font-normal text-muted-foreground"><Trans>requested a call</Trans></span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">{UtilApp.dateStrToTime(notif.created)}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide mb-1">
                <Users className="w-3.5 h-3.5" />
                <span>{call?.userIds.length} <Trans>Participants</Trans></span>
              </div>
              <h4 className="font-bold text-lg line-clamp-1 break-all">#{notif.roomName}</h4>
            </div>

            <div className="flex items-center gap-3">
              <UserAvatar
                src={notif.fromUser?.photoUrl}
                name={notif.fromUser?.name}
                className="w-10 h-10"
              />
              <div className="text-sm text-[color:rgb(var(--text-body))]/90 flex-1">
                <p className="leading-tight">
                  <span className="font-semibold">{notif.fromUser?.name}</span> <Trans>started a call</Trans>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{UtilApp.dateStrToTime(notif.created)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-1">
          <Button variant="outline" size="sm" onClick={dismissCall} className="w-full">
            <Trans>Dismiss</Trans>
          </Button>
          <Button size="sm" onClick={joinCall} className="w-full bg-green-600 hover:bg-green-700 text-on-primary-text">
            <Trans>Join call</Trans>
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  function dismissCall() {
    setCall(undefined);
    Fire.clearNotifications([notif]);
  }

  async function joinCall() {
    if (call?.room) {
      dismissCall();
      twil.join(call.room, My.user.id);
      history.replace(`/channel/${call.room.id}`);
    }
  }
}

export default CallInvitePopup;